"use client";

import { useEffect, useRef, type RefObject } from "react";
import { Pencil } from "@/components/icons";

import { IconButton } from "@/components/ui/icon-button";
import { Popover, PopoverAnchor } from "@/components/ui/popover";
import { InlineComboboxField, scopedLabel } from "./inline-combobox-field";
import { InlineComboboxPopover } from "./inline-combobox-popover";
import { CardTrigger, ValueTrigger } from "./inline-combobox-value-trigger";
import type { InlineComboboxProps } from "./inline-combobox-types";
import {
  useInlineCombobox,
  withInlineComboboxDefaults,
  type InlineComboboxController,
  type ResolvedInlineComboboxProps,
} from "./use-inline-combobox";

export type {
  InlineComboboxGroup,
  InlineComboboxOption,
} from "./inline-combobox-types";

interface InlineComboboxPartProps {
  props: ResolvedInlineComboboxProps;
  combobox: InlineComboboxController;
}

function InlineComboboxTrigger({
  props,
  combobox,
  inputRef,
}: InlineComboboxPartProps & { inputRef: RefObject<HTMLInputElement | null> }) {
  const { open, openAndSeed, openWithQuery, closedText } = combobox;
  const { value, onEdit, onClear, ariaLabel } = props;
  const customContent =
    !open && value && props.renderValue ? props.renderValue() : null;
  const triggerProps = {
    id: props.id,
    ariaLabel,
    ariaInvalid: props.ariaInvalid,
    ariaDescribedby: props.ariaDescribedby,
    disabled: props.disabled,
    className: props.className,
    onOpen: openAndSeed,
    onType: openWithQuery,
    content: customContent,
    closedText,
    placeholder: props.placeholder,
  };
  if (props.variant === "card" && !open) {
    return (
      <CardTrigger
        {...triggerProps}
        onEdit={value ? onEdit : undefined}
        onClear={value ? onClear : undefined}
        editLabel={scopedLabel("Edit", ariaLabel, "Edit selection")}
        clearLabel={scopedLabel("Remove", ariaLabel, "Remove selection")}
      />
    );
  }
  if (props.variant === "value" && !open)
    return <ValueTrigger {...triggerProps} />;
  return (
    <InlineComboboxField
      props={props}
      combobox={combobox}
      inputRef={inputRef}
      customContent={customContent}
    />
  );
}

function InlineComboboxEditButton({
  props,
  combobox,
}: InlineComboboxPartProps) {
  const { variant, disabled, value, onEdit, ariaLabel } = props;
  const showEdit =
    variant !== "value" &&
    variant !== "card" &&
    !disabled &&
    !combobox.open &&
    Boolean(value && onEdit);
  if (!showEdit) return null;
  const label = scopedLabel("Edit", ariaLabel, "Edit selection");
  return (
    <IconButton
      variant="outline"
      size={props.size}
      className="shrink-0"
      aria-label={label}
      title={label}
      onClick={onEdit}
    >
      <Pencil className="h-4 w-4" />
    </IconButton>
  );
}

function useFocusTriggerOnClose(open: boolean, anchorRef: RefObject<HTMLDivElement | null>, swapsTrigger: boolean) {
  const wasOpen = useRef(open);
  useEffect(() => {
    const closed = wasOpen.current && !open;
    wasOpen.current = open;
    if (!closed || !swapsTrigger) return;
    requestAnimationFrame(() => {
      if (document.activeElement && document.activeElement !== document.body) return;
      anchorRef.current?.querySelector<HTMLElement>("[role=combobox]")?.focus();
    });
  }, [open, anchorRef, swapsTrigger]);
}

export function InlineCombobox(inputProps: InlineComboboxProps) {
  const props = withInlineComboboxDefaults(inputProps);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const combobox = useInlineCombobox(props, inputRef);
  useFocusTriggerOnClose(combobox.open, anchorRef, props.variant === "card" || props.variant === "value");

  return (
    <Popover
      open={combobox.open}
      onOpenChange={(next) =>
        next ? combobox.openAndSeed() : combobox.setOpen(false)
      }
    >
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <PopoverAnchor asChild>
          <div ref={anchorRef} className="relative min-w-0 flex-1">
            <InlineComboboxTrigger
              props={props}
              combobox={combobox}
              inputRef={inputRef}
            />
          </div>
        </PopoverAnchor>
        <InlineComboboxEditButton props={props} combobox={combobox} />
      </div>
      <InlineComboboxPopover
        props={props}
        combobox={combobox}
        anchorRef={anchorRef}
      />
    </Popover>
  );
}
